import { type BufferGeometry, Color, Group, type Material, Mesh, MeshPhongMaterial, type MeshToonMaterial, type Object3D, Vector3 } from 'three';
import type { EraLook } from '../../config/eras';
import { PAL } from '../palette';
import { roundelTexture } from '../textures';
import { toonMaterial } from '../toon';
import { BODIES } from './bodies';
import { CarKit, carColors, SIDES } from './kit';
import { addDriver, headGeometry, Scarf, uprightGeometry, wheelGeometry } from './parts';

/**
 * A caricatured era racer and its big-headed driver. The body, trim and
 * driver merge into one vertex-coloured mesh; the number roundels, the head,
 * the scarf and each wheel are separate so they can move.
 * Local frame: facing +x, left = -z, ground at y = 0.
 */
export interface WheelRig {
  steer: Object3D;
  spin: Object3D;
  radius: number;
  front: boolean;
}

export interface CarModel {
  root: Group;
  /** Everything that squashes, pitches and rolls. */
  chassis: Group;
  head: Group;
  /** Rest position of the head in the chassis frame (the rig bobbles it around this). */
  headBase: Vector3;
  wheels: WheelRig[];
  /** The underside's corners (floor, wing tips, diffuser) in the chassis frame: however it leans, one of these is lowest. */
  lows: Vector3[];
  scarf: Scarf | null;
  materials: Material[];
}

/** The champion's gold (yesterday's winner races the day in a car made of it). */
const GOLD = {
  body: '#F2BC3E',
  deep: '#C48E24',
  helmet: '#F6C84E',
  bright: '#F3D57A',
  rim: '#F0C858',
  metal: '#E2B445',
  dark: '#C48F25',
  roundel: '#DDA53A',
};

/**
 * `gold`: yesterday's winner's car, made of gold for the day: body, wings, wheels, metalwork and helmet, shiny
 * where the rest of the world is flat paint. The tyres stay matte rubber, the scarf red, and the number is a 1.
 */
export function buildCar(look: EraLook, ghost = false, gold = false): CarModel {
  if (gold) look = { ...look, number: '1', helmetStripe: 'cream' };
  const c = carColors(look, ghost);
  if (gold) {
    c.main = new Color(GOLD.body);
    c.accent = new Color(GOLD.deep);
    c.stripe = new Color(GOLD.deep);
    c.helmet = new Color(GOLD.helmet);
    c.rim = new Color(GOLD.rim);
    c.disc = new Color(GOLD.dark);
    c.metal = new Color(GOLD.metal);
    c.bright = new Color(GOLD.bright);
    c.brass = new Color(GOLD.dark);
    c.ochre = new Color(GOLD.rim);
  }
  const kit = new CarKit(c);
  const layout = BODIES[look.body](kit);
  const driver = addDriver(kit, layout.driver, look.scarf);

  const matte = toonMaterial(0xffffff, { vertexColors: true, transparent: ghost });
  // Gold catches the sun: the bodywork (and the helmet) shine; the wheels keep the flat paint, so tyres stay rubber.
  const paint = gold
    ? new MeshPhongMaterial({ color: 0xffffff, vertexColors: true, transparent: ghost, shininess: 45, specular: new Color('#FFF4C8'), emissive: new Color('#2E1E04') })
    : matte;
  const numbers = toonMaterial(PAL.cream, { map: roundelTexture(look.number, gold ? GOLD.roundel : undefined), transparent: ghost });
  const materials: Material[] = gold ? [paint, matte, numbers] : [paint, numbers];
  const mesh = (m: Mesh, cast = true): Mesh => {
    m.castShadow = cast;
    m.receiveShadow = true;
    return m;
  };

  const root = new Group();
  const chassis = new Group();
  root.add(chassis);
  const body = kit.g.build();
  chassis.add(mesh(new Mesh(body, paint)));
  chassis.add(mesh(new Mesh(kit.decals.build(), numbers), false));

  const head = new Group();
  head.position.copy(driver.headBase);
  head.add(mesh(new Mesh(headGeometry(look.helmet, c), paint)));
  chassis.add(head);

  let scarf: Scarf | null = null;
  if (driver.scarfRoot) {
    scarf = new Scarf(c.scarf, driver.scarfRoot);
    chassis.add(scarf.mesh);
    const m = scarf.mesh.material as MeshToonMaterial;
    m.transparent = ghost;
    materials.push(m);
  }

  // Wheels hang off the root, not the chassis: they don't squash.
  const wheels: WheelRig[] = [];
  for (const [axle, front] of [
    [layout.rear, false],
    [layout.front, true],
  ] as const) {
    for (const s of SIDES) {
      const steer = new Group();
      steer.position.set(axle.x, axle.r, s * axle.z);
      const spin = new Group();
      steer.add(spin);
      spin.add(mesh(new Mesh(wheelGeometry(look.wheels, axle.r, axle.w, s, c), matte)));
      const upright = front ? uprightGeometry(look.wheels, axle.r, axle.w, s, c) : null;
      if (upright) steer.add(mesh(new Mesh(upright, matte)));
      root.add(steer);
      wheels.push({ steer, spin, radius: axle.r, front });
    }
  }
  return { root, chassis, head, headBase: driver.headBase.clone(), wheels, lows: lowPoints(body), scarf, materials };
}

/**
 * Tilted a little, a point of the body sits at about y + a·x + b·z, so the lowest vertex for each of a spread of
 * slopes (a, b) covers every lean the rig makes. Only what's near the bottom can ever be lowest.
 */
function lowPoints(geo: BufferGeometry): Vector3[] {
  const pos = geo.getAttribute('position');
  let bottom = Infinity;
  for (let i = 0; i < pos.count; i++) bottom = Math.min(bottom, pos.getY(i));
  const near: Vector3[] = [];
  for (let i = 0; i < pos.count; i++) if (pos.getY(i) < bottom + 0.5) near.push(new Vector3(pos.getX(i), pos.getY(i), pos.getZ(i)));
  const lows = new Set<Vector3>();
  for (let i = -5; i <= 5; i++) {
    for (let j = -5; j <= 5; j++) {
      const a = i * 0.04;
      const b = j * 0.04;
      let best = near[0];
      for (const p of near) if (p.y + a * p.x + b * p.z < best.y + a * best.x + b * best.z) best = p;
      lows.add(best);
    }
  }
  return [...lows];
}
